import type { UnitTypeShort } from 'dayjs'
import useUserStore from '@/composables/store/useUserStore'
import { Timestamp } from 'firebase/firestore'

const symbolMap: { [key: number]: string } = {
  0: '〇',
  1: '一',
  2: '二',
  3: '三',
  4: '四',
  5: '五',
  6: '六',
  7: '七',
  8: '八',
  9: '九',
  10: '十',
  11: '十一',
  12: '十二',
  13: '十三',
  14: '十四',
  15: '十五',
  16: '十六',
  17: '十七',
  18: '十八',
  19: '十九',
  20: '二十',
  21: '二十一',
  22: '二十二',
  23: '二十三',
  24: '二十四',
  25: '二十五',
  26: '二十六',
  27: '二十七',
  28: '二十八',
  29: '二十九',
  30: '三十',
  31: '三十一',
}

// const numberMap = {
//   〇: '0',
//   一: '1',
//   二: '2',
//   三: '3',
//   四: '4',
//   五: '5',
//   六: '6',
//   七: '7',
//   八: '8',
//   九: '9',
// }

export default function () {
  const dayjs = useDayjs()
  const { userTimezone } = useUserStore()

  const convertNumberToChinese = (digits: number) => {
    return digits.toString().split('').map(v => symbolMap[Number(v)]).join('')
  }

  dayjs.updateLocale('zh-tw', {
    ordinal: (number: number, period: string) => {
      switch (period) {
        case 'W':
          return `${number}週`
        default:
          return `${convertNumberToChinese(number)}日`
      }
    },
    relativeTime: {
      future: '%s內',
      past: '%s前',
      s: '剎那間',
      m: '1 分鐘',
      mm: '%d 分鐘',
      h: '1 小時',
      hh: '%d 小時',
      d: '1 天',
      dd: '%d 天',
      M: '1 個月',
      MM: '%d 個月',
      y: '1 年',
      yy: '%d 年',
    },
  })

  const normalizeDateInput = (input?: number | Timestamp | string | Date) => {
    if (!input)
      return Date.now()

    if (input instanceof Timestamp)
      return input.seconds * 1000

    return input
  }

  const createDayjs = (input?: number | Timestamp | string | Date, timezone = 'Asia/Taipei') => {
    const normalizedInput = normalizeDateInput(input)
    return dayjs(normalizedInput).tz(timezone || userTimezone)
  }

  const toDateString = (timestamp: Timestamp | number | undefined | Date, timezone?: string | undefined, format = 'YYYY-MM-DD') => {
    if (!timestamp)
      return ''
    return createDayjs(timestamp, timezone).format(format)
  }

  const timeFromNow = (timestamp: number, withoutSuffix = false) => {
    return createDayjs(timestamp).fromNow(withoutSuffix)
  }

  const timeDiff = (timestamp1: number, timestamp2: number, unit: UnitTypeShort, floatNumber = false) => {
    // day         d
    // week        w
    // quarter     Q
    // month       M
    // year        y
    // hour        h
    // minute      m
    // second      s
    // millisecond ms
    const endTime = createDayjs(timestamp1)
    const startTime = createDayjs(timestamp2)
    return endTime.diff(startTime, unit, floatNumber)
  }

  const parseToTimestamp = (datestring: string) => {
    return createDayjs(datestring).valueOf()
  }

  const isToday = (timestamp: number | Timestamp | Date) => {
    return createDayjs(timestamp).isToday()
  }

  const toDayObject = (timestamp: number | string, timezone?: string) => {
    const d = createDayjs(timestamp, timezone)

    return {
      year: d.year(),
      month: d.month() + 1,
      day: d.date(),
      weekday: d.day(),
    }
  }

  const toDayStringObject = (timestamp: number | string, timezone?: string, useChineseNumbers = true) => {
    const d = toDayObject(timestamp, timezone)

    return {
      year: useChineseNumbers ? convertNumberToChinese(d.year) : d.year.toString(),
      month: useChineseNumbers ? convertNumberToChinese(d.month) : d.month.toString(),
      day: useChineseNumbers ? convertNumberToChinese(d.day) : d.day.toString(),
      weekday: useChineseNumbers ? (d.weekday === 0 ? '日' : convertNumberToChinese(d.weekday)) : d.weekday.toString(),
      numeral: d,
    }
  }

  const getDateRangeOfDay = (datestring: string) => {
    const d = createDayjs(datestring)
    const startDate = d.startOf('day').valueOf()
    const endDate = d.endOf('day').valueOf()
    return [startDate, endDate]
  }

  return {
    dayjs,
    createDayjs,
    toDateString,
    toDayObject,
    toDayStringObject,
    timeFromNow,
    timeDiff,
    parseToTimestamp,
    isToday,
    convertNumberToChinese,
    getDateRangeOfDay,
  }
}
